import React, { useEffect, useState } from "react";
import { TagsInput } from "react-tag-input-component";

import { StateLanguage } from "../../languages/config/StateLanguage";
import { SettingsDA } from "../../services/admin/settings.service";
import { Error } from "../../models/error.model";

import { GlobalSwal } from "../../styles/swal.style";
import { legendInvalid, legendIconInvalid, legendIconValidInvalidRestart, legendValidInvalidRestart } from "../../utils/legend.data.util";
import { modalHide } from "../../utils/modal.util";
import { evaluateLegendRequiredIcon } from "../../scripts/validate.legend.script";
import { expressions } from "../../libraries/regular.expressions.library";
import { getError, getMessage } from "../../languages/translations/response";
import { errorWebHandler } from "../../scripts/error.web.handler.script";
import { Feature, FeatureInputType, FeatureType } from "../../models/settings.features.model";
import { Company } from "../../models/company.model";

import AppLegend from "../../components/element/Legend";
import AppInputFeatureType from "../element/InputFeatureType";

export interface AppModalSettingsFeatureProps {
    companyForUser: Company | null;
    featureItem: Feature | null;
    onSuccess: ( mode: "create" | "edit") => void;
}

let errorResponse: Error;

const AppModalSettingsFeature: React.FunctionComponent<AppModalSettingsFeatureProps> = ({companyForUser, featureItem, onSuccess }) => {
    const { lang } = StateLanguage();

    const [loadIndicator, setLoadIndicator] = useState("off");
    const [type, setType] = useState({ value: "", valid: false });
    const [name, setName] = useState({ value: "", valid: false });
    const [input_type, setInputType] = useState({ value: "select", valid: true });
    const [options, setOptions] = useState({ value: [] as string[], valid: false });

    const isEditMode = featureItem !== null;

    const submitFeature = async () => {
        setLoadIndicator("on");

        const optionsRequired = input_type.value === 'select'
        const optionsToSend = optionsRequired ? options.value : []

        if (companyForUser && type.valid && name.valid && (!optionsRequired || options.valid)) {
            const request = isEditMode
                ? SettingsDA.putSettingFeature(companyForUser.id,featureItem!.id, type.value, name.value, optionsToSend)
                : SettingsDA.postSettingFeature(companyForUser.id, type.value as FeatureType, name.value, input_type.value as FeatureInputType, optionsToSend);

            await request
                .then((response) => {
                  if (response.status === 200 || response.status === 201) {
                      const mode = isEditMode ? "edit" : "create";
                        onSuccess(mode);

                        GlobalSwal.fire({
                            title: isEditMode ? getMessage("Successfully updated feature", lang.code) : getMessage("Successfully created feature", lang.code),
                            text: lang.labels.actionCompletedReturningToPage,
                            icon: "success",
                            showConfirmButton: false,
                            timer: 2200,
                        }).then(() => {
                            executeHideModal();
                        });
                    } else {
                        errorResponse = response.data;

                        GlobalSwal.fire({
                            title: getError(errorResponse.code, lang.code, errorResponse.message),
                            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                            icon: "error",
                            confirmButtonText: lang.labels.okGotIt,
                        });
                    }
                })
                .catch((error) => {
                    errorWebHandler(lang, error);
                })
                .finally(() => {
                    setLoadIndicator("off");
                });
        } else {
            GlobalSwal.fire({
                text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
                icon: "error",
                showConfirmButton: false,
                timer: 2200,
            }).then(() => {
                if (!type.value.length) legendInvalid("legend-feature-type-required");
                if (!name.value.length)
                    legendIconInvalid("modal-settings-feature-input-name", "modal-settings-feature-legend-name-required");

                setLoadIndicator("off");
            });
        }
    };

    const handleChangeInputType = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setInputType({ value: event.target.value, valid: true });
        if (event.target.value === 'free') {
            setOptions({ value: [], valid: false });
        }
    };

    const handleChangeName = (event: React.ChangeEvent<HTMLFormElement | HTMLInputElement>) => {
        setName({ ...name, value: event.target.value });
    };

    const handleChangeOptions = (items: string[]) => {
        setOptions({ ...options, value: items });
    };

    const validateName = () => {
        evaluateLegendRequiredIcon(
            expressions.text,
            name,
            setName,
            "modal-settings-feature-input-name",
            "modal-settings-feature-legend-name-valid",
            "modal-settings-feature-legend-name-required",
        );
    };

    const validateOptionsKeyUp = (event: React.KeyboardEvent<HTMLFormElement | HTMLInputElement>) => {
        if (expressions && expressions.tag.test(event.currentTarget.value)) {
            if (!options.value.includes(event.currentTarget.value)) {
                setOptions({ ...options, valid: true });
            } else {
                setOptions({ ...options, valid: false });
                legendInvalid("modal-settings-feature-legend-options-exists");
            }
        } else {
            setOptions({ ...options, valid: false });
            legendInvalid("modal-settings-feature-legend-options-valid");
        }
    };

    const validateOptionsBlur = (event: React.FocusEvent<HTMLFormElement | HTMLInputElement>) => {
      setOptions({ ...options, valid: true });

      event.currentTarget.value = "";

      legendValidInvalidRestart("modal-settings-feature-legend-options-valid");
      legendValidInvalidRestart("modal-settings-feature-legend-options-required");
    };

    const validateOptionsAdd = (item: string, items: string[]) => {
        if (expressions && items) {
            if (expressions.tag.test(item)) {
                setOptions({ ...options, valid: true });
                return true;
            } else {
                setOptions({ ...options, valid: false });
                legendInvalid("modal-settings-feature-legend-options-valid");
                return false;
            }
        } else {
            setOptions({ ...options, valid: false });
            legendValidInvalidRestart("modal-settings-feature-legend-options-valid");
            legendValidInvalidRestart("modal-settings-feature-legend-options-required");
            return false;
        }
    };

    const validateOptionsExisting = (item: string) => {
        if (item.length > 0) {
            setOptions({ ...options, valid: false });
            legendInvalid("modal-settings-feature-legend-options-exists");
        } else {
            setOptions({ ...options, valid: true });
        }
    };

    const validateOptionsRemoved = (item: string) => {
        if (item.length > 0 && options.value.length === 1) {
            setOptions({ ...options, valid: false });
            legendInvalid("modal-settings-feature-legend-options-required");
        } else {
            setOptions({ ...options, valid: true });
        }
    };

    const executeHideModal = () => {
        modalHide("modal-settings-feature");

        setTimeout(() => {
            restartModal();
        }, 200);
    };

    function restartModal() {
        legendValidInvalidRestart("legend-feature-type-required");
        legendIconValidInvalidRestart("modal-settings-feature-input-name", "modal-settings-feature-legend-name-valid");
        legendIconValidInvalidRestart("modal-settings-feature-input-name", "modal-settings-feature-legend-name-required");
        legendValidInvalidRestart("modal-settings-feature-legend-options-valid");
        legendValidInvalidRestart("modal-settings-feature-legend-options-required");
        legendValidInvalidRestart("modal-settings-feature-legend-options-exists");

        setType({ value: "", valid: false });
        setName({ value: "", valid: false });
        setInputType({ value: "select", valid: true });
        setOptions({ value: [], valid: false });
    }

    useEffect(() => {
        if (featureItem) {
            const featureInputType = featureItem.input_type ?? 'select';
            setType({ value: featureItem.type, valid: true });
            setName({ value: featureItem.name, valid: true });
            setInputType({ value: featureInputType, valid: true });
            setOptions({ value: featureItem.options ?? [], valid: featureInputType === 'free' ? true : ((featureItem.options?.length ?? 0) > 0) });
        } else {
            restartModal();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [featureItem]);

    return (
        <div
            id="modal-settings-feature"
            className="modal fade"
            tabIndex={-1}
            aria-hidden="true"
            data-bs-backdrop="static"
            data-bs-keyboard="false"
        >
            <div className="modal-dialog modal-md">
                <div className="modal-content">
                    <div className="modal-header">
                        <h2 className="text-capitalize">
                            {isEditMode ? lang.labels.editFeatureParameter : lang.labels.addFeatureParameter}
                        </h2>
                    </div>
                    <div className="modal-body">
                        <div className="form">
                            <div className="row mb-2">
                                <label className="col-lg-3 col-form-label required">{lang.labels.type}</label>
                                <div className="col-lg-9">
                                    <AppInputFeatureType options={null} typeFeature={type} setTypeFeature={setType} />
                                </div>
                            </div>
                            <div className="row mb-2">
                                <label className="col-lg-3 col-form-label required">{lang.labels.name}</label>
                                <div className="col-lg-9">
                                    <input
                                        id="modal-settings-feature-input-name"
                                        className="form-control"
                                        type="text"
                                        autoComplete="off"
                                        name="name"
                                        value={name.value}
                                        onChange={handleChangeName}
                                        onKeyUp={validateName}
                                        onBlur={validateName}
                                    />
                                    <AppLegend
                                        component={"modal-settings-feature"}
                                        attribute={{ check: name.valid, name: "name", index: null, sub_index: null }}
                                        container={{ valid: true, required: true, identical: false, exists: false }}
                                    />
                                </div>
                            </div>
                            <div className="row mb-2">
                                <label className="col-lg-3 col-form-label required">{lang.labels.inputMethod}</label>
                                <div className="col-lg-9">
                                    <select className="form-select" value={input_type.value} onChange={handleChangeInputType}>
                                        <option value="select">{lang.labels.predefinedValues}</option>
                                        <option value="free">{lang.labels.freeInput}</option>
                                    </select>
                                </div>
                            </div>
                            {input_type.value === 'select' && <div className="row">
                                <label className="col-lg-3 col-form-label">{lang.labels.options}</label>
                                <div className="col-lg-9">
                                    <div className="options-cell">
                                        <TagsInput
                                            classNames={{ input: "form-control", tag: "fs-8 ps-2" }}
                                            value={options.value}
                                            onChange={handleChangeOptions}
                                            onKeyUp={(event: any) => validateOptionsKeyUp(event)}
                                            onBlur={(event: any) => validateOptionsBlur(event)}
                                            beforeAddValidate={(tag: any, existing: any) => validateOptionsAdd(tag, existing)}
                                            onExisting={(tag: any) => validateOptionsExisting(tag)}
                                            onRemoved={(tag: any) => validateOptionsRemoved(tag)}
                                        />
                                    </div>
                                    <AppLegend
                                        component={"modal-settings-feature"}
                                        attribute={{ check: options.valid, name: "feature-options", index: null, sub_index: null }}
                                        container={{ valid: true, required: false, identical: false, exists: true }}
                                    />
                                </div>
                            </div>}
                        </div>
                    </div>
                    <div className="modal-footer flex-center">
                        <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModal}>
                            {lang.labels.discard}
                        </button>
                        <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={submitFeature}>
                            <span className="indicator-label">{isEditMode ? lang.labels.saveChanges : lang.labels.create}</span>
                            <span className="indicator-progress">
                                {lang.labels.pleaseWait}
                                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AppModalSettingsFeature;
